import type { Theme } from '@goodtime/engine';
import acornUrl from '../assets/reel/forest/acorn.png';
import deerUrl from '../assets/reel/forest/deer.png';
import flowerUrl from '../assets/reel/forest/flower.png';
import foxUrl from '../assets/reel/forest/fox.png';
import leafUrl from '../assets/reel/forest/leaf.png';
import moonflowerScatterUrl from '../assets/reel/forest/moonflower_scatter.png';
import mushroomUrl from '../assets/reel/forest/mushroom.png';
import owlUrl from '../assets/reel/forest/owl.png';
import vineWildUrl from '../assets/reel/forest/vine_wild.png';
import environmentUrl from '../assets/environment/forest.png';
import columnBgUrl from '../assets/reel/forest_column_bg.png';
import borderUrl from '../assets/brand/border_forest.png';
// The bonus symbol — the multiplayer Reel Race's collect-token icon
// dropped into the slot grid where the server says it landed. The
// commissioned art (gold coin with oak-leaf relief) was generated via
// the toolgen pipeline (./gradlew generateAssets -Pargs="--asset
// reel:forest:bonus") off the brief in AssetRegistry.
import bonusUrl from '../assets/reel/forest/bonus.png';
// Item-drop reel symbol — the "incoming item, identity unknown" sprite
// that lands on the reel when a loadout item drops mid-Reel-Race spin
// (server inserts {@code ITEM_DROP_SYMBOL_ID} at the drop cell,
// mirroring how it inserts BONUS_SYMBOL_ID for bonus cells). A wrapped
// gift box (commissioned via the toolgen pipeline against the
// SYMBOL_STYLE_REFERENCE) so it reads as "mystery reward" without
// aliasing the BalancePill items chip OR the literal LUCKY_STAR
// item — both of which are gold stars and were colliding visually
// when the placeholder also used star.png.
import itemDropUrl from '../assets/reel/forest/item_drop.png';
// Veo-generated transparent celebration videos. Each WebM is the
// per-symbol "winning cell" celebration; the engine plays it once
// on win and holds the last painted frame as the trophy pose until
// the reel scrolls a new symbol into the cell. Generated server-side
// via the toolgen pipeline (./gradlew generateAllWinAnims, see
// apps/server/README.md). Vite's `?url` suffix gives the served URL;
// AssetLoader.loadTheme detects the .webm extension and routes it
// through the HTMLVideoElement loader.
import acornWinVideoUrl from '../assets/reel/forest/acorn_win.webm?url';
import deerWinVideoUrl from '../assets/reel/forest/deer_win.webm?url';
import flowerWinVideoUrl from '../assets/reel/forest/flower_win.webm?url';
import foxWinVideoUrl from '../assets/reel/forest/fox_win.webm?url';
import leafWinVideoUrl from '../assets/reel/forest/leaf_win.webm?url';
import moonflowerScatterWinVideoUrl from '../assets/reel/forest/moonflower_scatter_win.webm?url';
import mushroomWinVideoUrl from '../assets/reel/forest/mushroom_win.webm?url';
import owlWinVideoUrl from '../assets/reel/forest/owl_win.webm?url';
import vineWildWinVideoUrl from '../assets/reel/forest/vine_wild_win.webm?url';
import bonusWinVideoUrl from '../assets/reel/forest/bonus_win.webm?url';
import { withHevcCopies } from './slotAssets';

/**
 * Whispering Woods — the forest-biome themed slot. Pulls the
 * commissioned art batch ({@code apps/mobile/src/assets/reel/forest/})
 * into the engine's slot machine via {@link Theme#assets}.
 *
 * <p>Symbol id mapping is intentional: the engine's slot logic keys
 * off the same nine ids ({@code cherry, lemon, orange, bell, star,
 * seven, gem, scatter, money}) across every theme so payline maths
 * and bonus triggers stay portable. The visible art changes; the
 * physics don't.
 *
 * <p>Mapping rationale (common → rare):
 * <ul>
 *   <li>{@code cherry → flower} — most common forest object
 *   <li>{@code lemon → mushroom}, {@code orange → leaf} — common pickups
 *   <li>{@code bell → acorn} — mid-tier
 *   <li>{@code star → vine_wild} — the wild (named to match)
 *   <li>{@code seven → deer}, {@code gem → owl} — rare premium fauna
 *   <li>{@code scatter → moonflower_scatter} — the scatter (named to match)
 *   <li>{@code money → fox} — the fan-favorite cute, triggers hold-and-spin
 * </ul>
 */
export const FOREST_THEME: Theme = {
  id: 'forest',
  name: 'Whispering Woods',
  palette: {
    accent: 0x9de54e,         // mossy lime
    secondary: 0x4a8c5a,      // deep forest green
    warm: 0xffd23b,           // warm sunlight yellow (win highlight)
    frameFill: 0x1d2a1c,      // dark loam
    bgGradientTop: 0x0e1a0e,
    bgGradientBottom: 0x2a4a2a,
    dimOverlay: 0x05080d,
  },
  slot: {
    symbols: [
      { id: 'cherry',  label: 'Flower',     glyph: '🌸', tint: 0xff9bbf, weight: 5,   textureAlias: 'forest:cherry',
        winVideoAlias: 'forest:cherry:win:video', winVideoSpeed: 2 },
      { id: 'lemon',   label: 'Mushroom',   glyph: '🍄', tint: 0xc85a3a, weight: 5,   textureAlias: 'forest:lemon',
        winVideoAlias: 'forest:lemon:win:video', winVideoSpeed: 2 },
      { id: 'orange',  label: 'Leaf',       glyph: '🍃', tint: 0x6bbf5a, weight: 4,   textureAlias: 'forest:orange',
        winVideoAlias: 'forest:orange:win:video', winVideoSpeed: 2 },
      { id: 'bell',    label: 'Acorn',      glyph: '🌰', tint: 0x9c6f3d, weight: 3,   textureAlias: 'forest:bell',
        winVideoAlias: 'forest:bell:win:video', winVideoSpeed: 2 },
      { id: 'star',    label: 'Vine',       glyph: '🌿', tint: 0x9de54e, weight: 2,   textureAlias: 'forest:star', // wild
        winVideoAlias: 'forest:star:win:video', winVideoSpeed: 2 },
      { id: 'seven',   label: 'Deer',       glyph: '🦌', tint: 0x8a5a3a, weight: 1,   textureAlias: 'forest:seven',
        winVideoAlias: 'forest:seven:win:video', winVideoSpeed: 2 },
      { id: 'gem',     label: 'Owl',        glyph: '🦉', tint: 0xc8a45a, weight: 1,   textureAlias: 'forest:gem',
        winVideoAlias: 'forest:gem:win:video', winVideoSpeed: 2 },
      { id: 'scatter', label: 'Moonflower', glyph: '🌼', tint: 0xfff59d, weight: 1,   textureAlias: 'forest:scatter',
        winVideoAlias: 'forest:scatter:win:video', winVideoSpeed: 2 },
      { id: 'money',   label: 'Fox',        glyph: '🦊', tint: 0xff8a3d, weight: 1.5,
        textureAlias: 'forest:money',
        winVideoAlias: 'forest:money:win:video', winVideoSpeed: 2,
        isMoney: true },
      // Gem-on-reel — producer brief 2026-06-04. Scatter-style bonus
      // shared across every biome (registered via SHARED_REEL_SYMBOLS
      // in themes/index.ts). Weights deliberately rare so a gem
      // landing feels like a treat rather than background noise.
      { id: 'gem_t5',  label: 'Gems',     glyph: '💎', tint: 0x3ea7e8, weight: 0.10, gemValue: 5,  textureAlias: 'shared:reel:gem_5' },
      { id: 'gem_t10', label: 'Gem Pile', glyph: '💎', tint: 0x3ea7e8, weight: 0.05, gemValue: 10, textureAlias: 'shared:reel:gem_10' },
      // Bonus symbol — server-driven, lands probabilistically in the
      // multiplayer Reel Race phase. Weight is irrelevant (server
      // chooses placement); kept at 0 so any local-mode RunSpinLogic
      // fallback never rolls it natively.
      { id: 'bonus',   glyph: '✦', tint: 0xffc940, weight: 0,
        textureAlias: 'forest:bonus',
        winVideoAlias: 'forest:bonus:win:video', winVideoSpeed: 2 },
      // Item-drop landing symbol — same server-driven pattern as the
      // bonus coin: when the Reel-Race spin awards a loadout item,
      // the server replaces the rolled symbol at the chosen cell
      // with this id so the reel visibly lands a star at that
      // position. Weight 0 (server picks placement). The host-side
      // cycle-and-settle reveal (SlotMachine.playItemDropReveal)
      // takes over after the reel lands. No winVideoAlias — the
      // symbol never settles a payline.
      { id: 'item_drop', glyph: '★', tint: 0xffd86b, weight: 0,
        textureAlias: 'forest:item_drop' },
    ],
    wildSymbolId: 'star',
    scatterSymbolId: 'scatter',
  },
  wheel: {
    // Wheel segments stay the same shape across themes — the slot
    // game has its own mini-wheel for bonuses; the multiplayer Wheel
    // of Fortune room renders separately. v0 just tints these to match.
    segments: [
      { id: 'x1-a',   kind: 'multiplier', label: '×1',  tint: 0x9de54e, multiplier: 1,  weight: 4 },
      { id: 'x2-a',   kind: 'multiplier', label: '×2',  tint: 0x4a8c5a, multiplier: 2,  weight: 3 },
      { id: 'x0-a',   kind: 'multiplier', label: '✕',   tint: 0x1d2a1c, multiplier: 0,  weight: 2 },
      { id: 'x3-a',   kind: 'multiplier', label: '×3',  tint: 0xc8a45a, multiplier: 3,  weight: 2 },
      { id: 'x1-b',   kind: 'multiplier', label: '×1',  tint: 0x9de54e, multiplier: 1,  weight: 3 },
      { id: 'x5-a',   kind: 'multiplier', label: '×5',  tint: 0xffd23b, multiplier: 5,  weight: 1 },
      { id: 'x0-b',   kind: 'multiplier', label: '✕',   tint: 0x1d2a1c, multiplier: 0,  weight: 2 },
      { id: 'x10-a',  kind: 'multiplier', label: '×10', tint: 0xfff59d, multiplier: 10, weight: 1 },
      { id: 'x2-b',   kind: 'multiplier', label: '×2',  tint: 0x4a8c5a, multiplier: 2,  weight: 3 },
      { id: 'x1-c',   kind: 'multiplier', label: '×1',  tint: 0x9de54e, multiplier: 1,  weight: 3 },
      { id: 'x3-b',   kind: 'multiplier', label: '×3',  tint: 0xc8a45a, multiplier: 3,  weight: 2 },
      { id: 'jackpot',kind: 'multiplier', label: '×25', tint: 0xff8a3d, multiplier: 25, weight: 1 },
    ],
  },
  assets: {
    slot: {
      cherry:  { alias: 'forest:cherry',  src: flowerUrl, path: 'reel/forest/flower.png' },
      lemon:   { alias: 'forest:lemon',   src: mushroomUrl, path: 'reel/forest/mushroom.png' },
      orange:  { alias: 'forest:orange',  src: leafUrl, path: 'reel/forest/leaf.png' },
      bell:    { alias: 'forest:bell',    src: acornUrl, path: 'reel/forest/acorn.png' },
      star:    { alias: 'forest:star',    src: vineWildUrl, path: 'reel/forest/vine_wild.png' },
      seven:   { alias: 'forest:seven',   src: deerUrl, path: 'reel/forest/deer.png' },
      gem:     { alias: 'forest:gem',     src: owlUrl, path: 'reel/forest/owl.png' },
      scatter: { alias: 'forest:scatter', src: moonflowerScatterUrl, path: 'reel/forest/moonflower_scatter.png' },
      money:   { alias: 'forest:money',   src: foxUrl, path: 'reel/forest/fox.png' },
      bonus:   { alias: 'forest:bonus',   src: bonusUrl, path: 'reel/forest/bonus.png' },
      item_drop: { alias: 'forest:item_drop', src: itemDropUrl, path: 'reel/forest/item_drop.png' },
    },
    slotWin: withHevcCopies({
      // Per-symbol Veo-generated transparent celebration videos.
      // Authored idle-bracketed (start = end = sleeping/idle pose,
      // peak in the middle) so the engine plays the clip once on
      // win and then reveals the static slot symbol when the video
      // ends — the idle-end-frame matches the static, so the swap
      // is visually invisible.
      cherry:  { alias: 'forest:cherry:win:video',  src: flowerWinVideoUrl, path: 'reel/forest/flower_win.webm' },
      lemon:   { alias: 'forest:lemon:win:video',   src: mushroomWinVideoUrl, path: 'reel/forest/mushroom_win.webm' },
      orange:  { alias: 'forest:orange:win:video',  src: leafWinVideoUrl, path: 'reel/forest/leaf_win.webm' },
      bell:    { alias: 'forest:bell:win:video',    src: acornWinVideoUrl, path: 'reel/forest/acorn_win.webm' },
      star:    { alias: 'forest:star:win:video',    src: vineWildWinVideoUrl, path: 'reel/forest/vine_wild_win.webm' },
      seven:   { alias: 'forest:seven:win:video',   src: deerWinVideoUrl, path: 'reel/forest/deer_win.webm' },
      gem:     { alias: 'forest:gem:win:video',     src: owlWinVideoUrl, path: 'reel/forest/owl_win.webm' },
      scatter: { alias: 'forest:scatter:win:video', src: moonflowerScatterWinVideoUrl, path: 'reel/forest/moonflower_scatter_win.webm' },
      money:   { alias: 'forest:money:win:video',   src: foxWinVideoUrl, path: 'reel/forest/fox_win.webm' },
      bonus:   { alias: 'forest:bonus:win:video',   src: bonusWinVideoUrl, path: 'reel/forest/bonus_win.webm' },
    }),
    scene: {
      background:       { alias: 'forest:bg',         src: environmentUrl, path: 'environment/forest.png' },
      columnBackground: { alias: 'forest:column_bg',  src: columnBgUrl, path: 'reel/forest_column_bg.png' },
      border:           { alias: 'forest:border',     src: borderUrl, path: 'brand/border_forest.png' },
      // Win-tier treatments are attached at module load by
      // {@link ./index.ts} so all 5 themes share the same set without
      // duplicate import noise here.
    },
  },
};
